import React, {useContext, useState, useEffect} from "react";
import Modal from "../../ui-components/Modal";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import Button from "../../ui-components/Button";
import sharedApiRequest from "../../services/apiRequest";
import {NotificationsContext} from "../../ui-components/Notifications";
import {useIntl, FormattedMessage} from "react-intl";
import RadioGroup from "../../ui-components/RadioGroup";
import SearchSelect from "../../ui-components/SearchSelect";
import CountryContext from "../../CountryContext";
import { validateEmail } from "@utils/validate-email";

import _ from "lodash"
import {useConnectedUsers} from "@feature/users/connections";
import {ExclamationCircleIcon, InformationCircleIcon} from "@heroicons/react/24/outline";
import {checkExternalUser} from "@utils/checkExternalUser";
import {PRIVILEGED_ROLES} from "../../constants";

function WorksiteShareModal({closeModal, selectedWorksite, userData, onShared}) {
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();
  const {
    fetch: fetchConnectedUsers,
    loading: loadingConnectedUsers,
  } = useConnectedUsers();

  const {push} = useContext(NotificationsContext);
  const {country} = useContext(CountryContext);

  const [userType, setUserType] = useState();
  const [internalUsers, setInternalUsers] = useState();
  const [resales, setResales] = useState();
  const [selectedInternalUser, setSelectedInternalUser] = useState();
  const [selectedResale, setSelectedResale] = useState();
  const [canShare, setCanShare] = useState(true);
  const [externalUser, setExternalUser] = useState(undefined);
  const [sharing, setSharing] = useState(false);

  const showResale = [...PRIVILEGED_ROLES, 'field', 'area_manager', 'agent'].includes(userData.role_type)
  const isExternalUserEmailValid = !!externalUser && validateEmail(externalUser.value);
  
  const resalesOptions = resales?.map((i) => ({
    value: i,
    label: `${i.company}${!!i.company_address ? " (" + i.company_address + ")" : ""}`,
  })) || [];

  useEffect(() => {
    const fetchInternalUsers = () => {
      sharedApiRequest
        .get(`/users/internal?country=${country.code}`)
        .then((res) => {
          setInternalUsers(res);
        })
        .catch(() => {
          setInternalUsers([]);
          push({
            title: intl.formatMessage({id: "server_error"}),
            type: "error",
          });
        });
    };

    const fetchResales = () => {
      sharedApiRequest
        .get(`/users/resales?country=${country.code}`)
        .then((res) => {
          setResales(res);
        })
        .catch(() => {
          setResales([]);
          push({
            title: intl.formatMessage({id: "server_error"}),
            type: "error",
          });
        });
    };

    if (!internalUsers && userType === "internal") {
      fetchInternalUsers();
    }

    if (showResale && !resales && userType === "resale") {
      fetchResales();
    }
    // eslint-disable-next-line
  }, [internalUsers, userType, push]);

  const shareWorksite = async () => {
    setSharing(true);
    try {

      // Determine if the user is already registered or not
      let sharingUser;
      switch (userType) {
        case "internal":
          sharingUser = selectedInternalUser["value"];
          break;
        case "resale":
          sharingUser = selectedResale["value"];
          break;
        case "external":
          if (!!externalUser.data) {
            // CASE A: user is among connected users --> share the worksite
            sharingUser = externalUser.data;
          }
          else {
            const {
              invite,
              internal,
              user,
            } = await checkExternalUser(externalUser.value, country.code);
            if (internal) {
              // CASE B: user is internal --> throw warning and exit
              push({type: "warning", title: intl.formatMessage({id: "info_sharing_is_internal"})});
              return;
            }
            else if (!!user)
              // CASE C: user is not among connected users but is registered --> share the worksite
              sharingUser = user;
            else if (invite)
              // CASE D: user is not among connected users and is not registered --> invite the user
              sharingUser = undefined;
            else
              throw new Error(`Unhandled case for external user (${{invite, internal, user}})`);
          }
          break;
        default:
          throw new Error(`Unhandled user type ${userType}`);
      }

      // Perform sharing/invitation
      if (sharingUser === undefined) {
        // This branch is executed only if the user is not registered yet (external users, case D): we need to invite him
        const data = {
          email: externalUser.value,
          country: country.code,
          worksite_id: selectedWorksite.id
        };
        await sharedApiRequest.post(`/users/invitate`, data);
        push({title: intl.formatMessage({id: "ok_shared"}), type: "success"});
      }
      else {
        // In this branch, the user is already registered: we can share the worksite
        const payload = {
          worksite_id: selectedWorksite.id,
          to_user_id: sharingUser["id"],
          to_user_name: sharingUser["firstname"] + " " + sharingUser["lastname"],
          hierarchy: sharingUser["role_hierarchy"],
          email: sharingUser["email"],
        }
        const {message} = await apiRequest.post('/share', payload);
        if (message === 'already_shared') {
          push({title: intl.formatMessage({id: "already_shared"}), type: "success"});
        } else {
          await onShared();
          push({title: intl.formatMessage({id: "ok_shared"}), type: "success"});
        }
      }

      closeModal();

    }
    catch (error) {
      push({
        title: intl.formatMessage({id: "server_error"}),
        type: "error",
      });
      throw error;
    }
    finally {
      setSharing(false);
    }
  };


  useEffect(() => {
    setSelectedInternalUser(undefined);
    setSelectedResale(undefined);
    setExternalUser(undefined);
  }, [userType]);

  useEffect(() => {
    setCanShare(
      userType &&
      ((userType === "internal" && selectedInternalUser) ||
        (userType === "resale" && selectedResale) ||
        (userType === "external" && externalUser && isExternalUserEmailValid))
    );
  }, [userType, selectedInternalUser, selectedResale, externalUser]);

  const getLabel = (i) => {
    return `${i["firstname"]} ${i["lastname"]} (${intl.formatMessage({id: i.role.name})})`;
  };

  return (
    <Modal onExit={closeModal} overflowCls="">
      <div className="flex w-full flex-col gap-3 h-full items-center justify-center">
        <label className="block text-m text-black-500">
          <FormattedMessage id="share_with?"/>
        </label>
        <RadioGroup
          horizontal
          id="user-type-selection"
          options={(country.code === 'es' || userData.business !== 'kerakoll') ?
            [
              {value: "internal", name: intl.formatMessage({id: "internal_user"})}
            ] :
            showResale ?
              [
                {value: "internal", name: intl.formatMessage({id: "internal_user"})},
                {value: "resale", name: intl.formatMessage({id: "user_type_resale"})},
                {value: "external", name: intl.formatMessage({id: "external_user"})}
              ] :
              [
                {value: "internal", name: intl.formatMessage({id: "internal_user"})},
                {value: "external", name: intl.formatMessage({id: "external_user"})}
              ]
          }
          currentValue={userType}
          onChange={(value) => {
            setUserType(value);
          }}
        />
        {userType === "internal" && internalUsers && (
          <>
            <div className={"w-full mt-2"}>
              <SearchSelect
                value={selectedInternalUser}
                options={_.sortBy(internalUsers || [], i => i.lastname).map((i) => ({
                  value: i,
                  label: getLabel(i),
                }))}
                onChange={(e) => setSelectedInternalUser(e)}
              />
            </div>
            <label className="block text-xs text-black-500 m-4">
              <FormattedMessage id="info_sharing_registered"/>
            </label>
          </>
        )}
        {userType === "resale" && resales && (
          <>
            {resalesOptions.length ? <div>
              <div className={"w-full mt-2"}>
                <SearchSelect
                  value={selectedResale}
                  options={_.sortBy(resalesOptions, i => i.label)}
                  onChange={(e) => setSelectedResale(e)}
                />
              </div>
              <label className="block text-xs text-black-500 m-4">
                <FormattedMessage id="info_sharing_resales"/>
              </label>
            </div> : <div>
              <label className="block text-xs text-black-500 m-4">
                <FormattedMessage id="no_active_resales"/>
              </label>
            </div>
            }
          </>
        )}
        {userType === "external" && <>
          <div className="w-full">
            <SearchSelect
              label={<FormattedMessage id="insert_user" defaultMessage="Inserisci l'utente" />}
              loadOptions={async (search, callback) => {
                const results = await fetchConnectedUsers(search);
                const options = results
                  ?.map((user) => {
                    const {email, firstname, lastname} = user;
                    return {
                      label: `${firstname} ${lastname}`,
                      value: email,
                      data: user,
                    }
                  })
                  || [];
                callback(options.length > 0 ? options : [{label: search, value: search, data: null}]);
              }}
              value={externalUser}
              onChange={(selected) => { setExternalUser(selected); }}
              CustomOption={({data: {label, value, data}, innerProps, innerRef}) => {
                return (
                  <article
                    ref={innerRef}
                    className="py-2 px-3 cursor-pointer hover:bg-blue-50"
                    {...innerProps}
                  >
                    {!!data && <p className="font-semibold text-sm">{label}</p>}
                    <p className={!data ? "text-sm" : "text-xs"}>
                      {value}
                      {!data && (
                        <span className="text-xxs text-white uppercase font-semibold rounded bg-am-700 ml-2 px-1 py-0.5">
                          <FormattedMessage id="add" />
                        </span>
                      )}
                    </p>
                  </article>
                )
              }}
              isLoading={loadingConnectedUsers}
              isClearable
            />
            <div className="text-xs text-gray-400">
              <InformationCircleIcon className="w-3 inline-block mr-1 -mt-0.5" />
              <FormattedMessage
                id="share_with_external_user_helper"
                defaultMessage={
                  "Cerca l'utente con il quale vuoi condividere il cantiere utilizzando il campo di ricerca, " +
                  "digitandone il nome o l'indirizzo email. Nel caso in cui non compaia nella lista, potrai " +
                  "comunque digitare l'indirizzo email e confermare l'operazione"
                }
              />
            </div>
            {!!externalUser && !validateEmail(externalUser.value) && (
              <div className="flex flex-row items-center gap-1.5 text-red-500 text-sm mt-3">
                <ExclamationCircleIcon className="w-5" />
                <FormattedMessage
                  id="invalid_email"
                  defaultMessage="L'indirizzo email inserito non è valido"
                />
              </div>
            )}
          </div>
        </>}
        {canShare && (
          <div className="w-full mt-2 flex justify-center">
            <Button
              variant="contained"
              color="primary"
              onClick={shareWorksite}
              submitting={sharing}
            >
              <FormattedMessage id="share"/>
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default WorksiteShareModal;
